export const translations = {
  en: {
    app: {
      profile: 'Profile',
      supportAndHelp: 'Support & Help',
      closeModal: 'Close modal',
    },
    header: {
      credits: 'Credits:',
      addCredits: 'Add Credits',
      upgrade: 'Upgrade',
    },
    tabbar: {
      create: 'Create',
    },
    offer: {
      buyCta: 'Get 5 Videos',
      registerTitle: 'Sign Up First',
      registerSubtitle: 'Create your account to continue',
      hero: {
        eyebrow: 'Limited-time starter package',
        proofText: '1,000+ people bought',
        proofRatingAria: '5 out of 5 stars',
        title: 'Cut Your Content Costs by 90%: Conversion-Driving UGC Videos in Your Pocket in 5 Minutes.',
        subtitle: 'Get the fastest way to test new hooks, personas, scripts, and languages without waiting on creators, shipping products, or managing a long production cycle.',
        highlights: [
          'Create 5 different vertical ad videos in one starter package',
          'Realistic AI UGC output with strong product integrity on screen',
          'Different personas, scenes, hooks, and script directions in one flow',
          'Fast creative refreshes, simpler iteration, and multi-language flexibility',
        ],
        summary: {
          label: 'Starter package price',
          oldPrice: '',
          note: 'One payment. Five videos. Built to show the product quickly and see the quality clearly.',
          badges: ['One-time package', 'Fast creative start', 'Product-focused output'],
          includesTitle: '',
          includes: [],
        },
      },
      results: {
        eyebrow: '',
        title: 'See our customers results',
        subtitle: '',
        note: '',
        tags: ['5 videos', 'Realistic AI UGC', 'Different angles'],
        items: [
          {
            badge: 'Realism',
            title: 'Show faces, motion, and natural delivery',
            text: 'Lead with outputs that do not feel fake. The first reaction should be: this looks usable for paid social.',
            visualLabel: 'Result video placeholder 01',
            visualNote: 'Replace with a real output video showing a convincing creator-style talking shot.',
          },
          {
            badge: 'Product integrity',
            title: 'Show the product staying intact on screen',
            text: 'Use examples where the product remains clean and recognizable even when the scene is generated around it.',
            visualLabel: 'Result video placeholder 02',
            visualNote: 'Replace with a sample where the uploaded product image remains stable and visually strong.',
          },
          {
            badge: 'Hook variety',
            title: 'Show different message angles quickly',
            text: 'Make it obvious that these are not five copies of one idea. Show different openings, claims, or emotional directions.',
            visualLabel: 'Result video placeholder 03',
            visualNote: 'Replace with a sample that highlights a different hook or ad angle from the first card.',
          },
          {
            badge: 'Format quality',
            title: 'Show jump cuts and b-roll pacing',
            text: 'The offer feels stronger when the videos already look structured like short-form ads instead of static raw clips.',
            visualLabel: 'Result video placeholder 04',
            visualNote: 'Replace with an output that clearly shows pacing, shot variation, or b-roll moments.',
          },
          {
            badge: 'Multi-language',
            title: 'Show how easily one concept can travel',
            text: 'If you want broader appeal, show that the same product can be presented in multiple languages without a full new production loop.',
            visualLabel: 'Result video placeholder 05',
            visualNote: 'Replace with a localized output or side-by-side language variation example.',
          },
        ],
      },
      problem: {
        eyebrow: 'Why this matters',
        title: 'Traditional UGC is slow, expensive, and hard to scale',
        subtitle: 'Every new video can mean more time, more coordination, and more cost. As the process slows down, fewer ideas get tested and winners appear later.',
        cards: [
          {
            icon: 'clock',
            title: 'Getting one new creative live can take days',
            text: 'Finding a creator, shipping the product, sharing the brief, waiting for the shoot, and collecting revisions turns a simple ad test into a long production cycle.',
          },
          {
            icon: 'warning',
            title: 'Persona, setting, and product fit rarely align',
            text: 'Even when a video gets made, it may still miss the mark. The persona may feel off, the environment may not fit, or the product may not look clean enough on screen.',
          },
          {
            icon: 'chart',
            title: 'Every new version adds more cost and more delay',
            text: 'A quality UGC video is already expensive in the traditional flow. More angles, new scripts, or multiple languages make testing even slower and more costly.',
          },
        ],
      },
      workflow: {
        eyebrow: 'How it works',
        title: 'Create realistic UGC in 3 simple steps',
        subtitle: 'Suitable for physical products, digital products, and services. Add the product, persona, scene, and script, then approve. The AI agent system prepares the video in around 5 minutes.',
        items: [
          {
            title: 'Enter product details',
            text: 'Enter the product name, product type, dream persona, dream environment, and upload the product visual you want to protect on screen.',
          },
          {
            title: 'Write what the creator should say',
            text: 'Shape the script, choose the direction, and define the angle you want the creator to communicate in the video.',
          },
          {
            title: 'Approve and generate',
            text: 'Confirm the setup and let the agent system produce the videos in minutes instead of dragging you into a long production cycle.',
          },
        ],
      },
      comparison: {
        eyebrow: 'Why 5UGC wins',
        title: 'Faster and easier with 5UGC',
        subtitle: 'You are not just buying five videos. You upload your product and get realistic videos in about 5 minutes through a simple 3-step flow. Your product stays more stable on screen, and you can test personas, angles, and languages much faster.',
        matrixLabel: 'Comparison',
        traditionalTitle: 'Traditional',
        aiTitle: '5UGC',
        rows: [
          'Test physical products and other offers faster across different personas and environments',
          'Get more realistic videos while keeping the uploaded product visual far more stable on screen',
          'Set up in 3 simple steps and get videos in about 5 minutes',
          'Generate jump cut, b-roll, and multi-language variations automatically',
          'Avoid paying $20-$60 per video and waiting days for each new angle',
        ],
      },
      package: {
        eyebrow: 'What you get',
        title: 'Everything inside the 5-video starter package',
        subtitle: 'This is the low-barrier offer designed to make someone try the product, see the quality, and want more.',
        priceLabel: 'Limited-time entry price',
        oldPrice: 'Traditional creator-style output can easily cost more than this for a single video',
        priceTag: 'One-time purchase',
        priceNote: 'Use this starter pack to see real output quality before any larger spend or longer-term commitment.',
        includesTitle: 'Included in your purchase',
        includes: [
          '5 AI UGC videos built for short-form ads and product marketing',
          'Support for multiple personas, environments, hooks, and script directions',
          'Realistic output quality designed to avoid the usual fake-AI look',
          'Product-first rendering logic that helps keep your uploaded visual stable',
          'Automatic short-form pacing with jump cuts and b-roll style moments',
          'A faster way to test whether 5UGC should become part of your workflow',
        ],
        reasonsTitle: 'Why this offer converts well',
        reasons: [
          'It is cheap enough to feel easy to try.',
          'It is strong enough to show the real promise of the product.',
          'It removes most of the complexity people expect from video production.',
          'It gives buyers a quick win before they decide anything bigger.',
        ],
        tags: ['5 videos', 'Realistic AI UGC', 'Product-safe feel'],
        footnote: 'This package is meant to bring users in with something genuinely attractive, not a watered-down teaser.',
      },
      testimonials: {
        eyebrow: 'Customer proof',
        title: 'What users say after trying 5UGC',
        subtitle: 'Short comments from brands and operators who used 5UGC to test faster, keep the product cleaner on screen, and launch new angles with less production effort.',
        items: [
          { quote: '“First test videos in minutes changed our speed.”', name: 'Selin K.', role: 'Performance marketer' },
          { quote: '“The product stayed much cleaner on screen than most AI tools.”', name: 'Mert A.', role: 'E-commerce founder' },
          { quote: '“We tested three different hooks in one afternoon.”', name: 'Derya T.', role: 'Growth lead' },
          { quote: '“The setup was simple enough for our team to use right away.”', name: 'Cem I.', role: 'Brand manager' },
          { quote: '“The videos felt realistic enough for paid ads.”', name: 'Ece Y.', role: 'Paid social specialist' },
          { quote: '“Different personas gave us faster signals.”', name: 'Burak S.', role: 'DTC operator' },
          { quote: '“Multi-language variations made testing new markets easier.”', name: 'Lina P.', role: 'Marketplace lead' },
          { quote: '“Jump cut pacing made the creatives feel more native.”', name: 'Onur D.', role: 'Creative strategist' },
          { quote: '“Product upload helped the ads stay on-brand.”', name: 'Zeynep N.', role: 'Content lead' },
          { quote: '“Five videos gave us enough variation to spot a winner quickly.”', name: 'Arda B.', role: 'Media buyer' },
          { quote: '“It was much cheaper than organizing another creator shoot.”', name: 'Melis C.', role: 'Small business owner' },
          { quote: '“We could test a new offer without slowing the whole team.”', name: 'Kaan E.', role: 'Growth manager' },
        ],
      },
      faq: {
        eyebrow: 'FAQ',
        title: 'Top questions people ask before buying 5UGC',
        subtitle: 'Clear answers on realism, product protection, speed, workflow, languages, and why this is especially strong for physical products without being limited to them.',
        items: [
          { question: 'Is 5UGC only for physical products?', answer: 'No. 5UGC can also be used for digital products and services. But it is especially strong for physical products because the workflow is built to keep the uploaded product more stable and recognizable on screen.' },
          { question: 'Will the videos look fake?', answer: 'Realism is one of the main strengths of the product. The goal is not generic AI video, but realistic UGC-style output that feels much closer to something you could actually test in ads.' },
          { question: 'Can my product distort inside the video?', answer: 'The system is designed to protect the uploaded product visual as much as possible. Because of the background processing we do behind the scenes, the product usually stays far cleaner and more stable than in typical AI video flows.' },
          { question: 'How fast are the videos created?', answer: 'Videos are typically generated in around 5 minutes. That speed is a core part of the product and one of the reasons the name is 5UGC.' },
          { question: 'Why is the workflow only 3 steps?', answer: 'Because a strong AI agent system handles the heavy lifting in the background. Instead of forcing users through a long setup, 5UGC reduces the process to three simple steps: setup, script, and approval.' },
          { question: 'What do I enter in the first step?', answer: 'In step one, you enter the product name, product type, dream persona, dream environment, and upload the product image you want to preserve inside the video.' },
          { question: 'What happens in step two and step three?', answer: 'In step two, you write what the creator should say and define the message angle. In step three, you review the setup and approve generation.' },
          { question: 'Are jump cuts and b-roll added automatically?', answer: 'Yes. Jump cut pacing and b-roll-style structure are handled automatically, so the output feels closer to a ready-to-test short-form ad instead of a flat raw clip.' },
          { question: 'Can I create videos in multiple languages?', answer: 'Yes. Multi-language generation is supported, which makes it easier to test the same offer or concept across different audiences without restarting the whole production process.' },
          { question: 'How does this compare to traditional UGC costs?', answer: 'Traditional creator workflows can easily cost $20-$30 per video at the low end, and much more for quality at the level 5UGC aims to deliver. That makes fast testing expensive very quickly in the traditional model.' },
          { question: 'Is this page selling a subscription?', answer: 'No. This page is a lower-friction one-time starter offer designed to get users into the product with a strong first experience. If you need ongoing volume later, subscription plans are the natural next step.' },
        ],
      },
      final: {
        eyebrow: 'Ready to test',
        title: 'If you want to see what 5UGC can really do, start here',
        subtitle: 'Buy the 5-video starter package, see the quality for yourself, and turn one small purchase into real creative proof in minutes.',
        note: 'Use any CTA on the page or the sticky button below whenever you are ready.',
      },
    },
    home: {
      library: 'Library',
      avatarsComingSoon: 'Avatars - Coming Soon',
      createCardTitle: 'New video',
      createCardSubtitle: 'Turn a new idea into UGC in minutes.',
      sorting: 'Sorting',
      loadingVideos: 'Videos are loading...',
      retry: 'Retry',
      couldNotLoadVideos: 'Could not load videos',
      createVideoCta: 'Create Video',
      legalPrefix: 'By continuing, you agree to our',
      legalAnd: 'and',
    },
    success: {
      iconAlt: 'Success',
      title: 'Your subscription is active',
      subtitle: 'Payment completed successfully. You can now use your weekly credits and continue creating UGC videos.',
      goHome: 'Go to Library',
    },
    profileCard: {
      user: 'User',
      avatar: 'Avatar',
      availableCredits: 'Available Credits',
      buyCredits: 'Buy Credits',
      upgradePlan: 'Upgrade Plan',
    },
    settings: {
      shareFeedback: 'Share Your Feedback',
      manageSubscription: 'Manage Subscription',
      changeLanguage: 'Change App Language',
      helpCenter: 'Help Center',
      supportChat: 'Support Chat',
      signOut: 'Sign Out',
      languageModalTitle: 'Change App Language',
      languageEnglish: 'English (US)',
      languageTurkish: 'Türkçe',
    },
    auth: {
      signIn: 'Sign In',
      signInSubtitle: 'Sign in to your account',
      signUp: 'Sign Up',
      signUpSubtitle: 'Create a new account',
      resetPassword: 'Reset Password',
      resetPasswordSubtitle: "We'll send a reset link to your email.",
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      forgotPassword: 'Forgot Password?',
      backToSignIn: 'Back to Sign In',
      email: 'Email',
      password: 'Password',
      fullName: 'Full Name',
      sendLink: 'Send Link',
      sending: 'Sending...',
      resendIn: 'Resend in {time}',
      fillAllFields: 'Please fill in all fields.',
      passwordMin: 'Password must be at least 8 characters.',
      creatingAccount: 'Creating account...',
      signingIn: 'Signing in...',
      accountCreatedCheckEmail: 'Account created! Please check your email.',
      enterEmail: 'Please enter your email address.',
      resetLinkSent: 'Password reset link has been sent to your email.',
      legalPrefix: 'By signing up, you agree to our',
      legalAnd: 'and',
      termsOfService: 'Terms of Service',
      privacyPolicy: 'Privacy Policy',
    },
    paywall: {
      subscription: 'Subscription',
      choosePlan: 'Choose Your Plan',
      unlockMore: 'Choose the plan that matches how many videos you want to create each week',
      creditInfo: 'Each video costs 10 credits.',
      continue: 'Continue',
      processing: 'Processing...',
      checkoutFailed: 'Could not open checkout. Please try again.',
      checkoutFailedInline: 'Payment could not be received. Please try again.',
      popular: 'Popular',
      level1: 'Level 1',
      level2: 'Level 2',
      level3: 'Level 3',
      weekly: '/week',
      level1Features: '2 videos/week',
      level2Features: '6 videos/week',
      level3Features: '23 videos/week',
    },
    subscriptionModal: {
      title: 'Your Subscription',
      currentPlan: 'Current plan',
      description: 'You can switch to a higher plan any time if you need more videos each week.',
      upgradeButton: 'Upgrade Subscription',
      manageButton: 'Manage Subscription',
      unknownPlan: 'Current Subscription',
    },
    videoModal: {
      closeVideoModal: 'Close video modal',
      untitledAsset: 'Untitled Asset',
      download: 'Download',
      shareVideo: 'Share video',
      deleteVideo: 'Delete video',
      noShareLinkForVideo: 'No share link available for this video.',
      linkCopiedClipboard: 'Video link copied to clipboard.',
      copyLinkManually: 'Could not copy link. Please copy it manually.',
      shareFailed: 'Share failed. Please try again.',
      type: 'Type',
      duration: 'Duration',
      audio: 'Audio',
      aspectRatio: 'Aspect Ratio',
      dimensions: 'Dimensions',
      generated: 'Generated',
      retention: 'Retention',
      included: 'Included',
      daysLeft: '{count} days left',
      dayLeft: '{count} day left',
      videoAsset: 'VIDEO ASSET',
      details: 'DETAILS',
      videoTypeMp4: 'Video / MP4',
    },
    wizard: {
      product: 'Product',
      persona: 'Persona',
      scene: 'Scene',
      script: 'Script',
      finalize: 'Finalize',
      back: 'Back',
      closeWizard: 'Close wizard',
      next: 'Next',
      confirm: 'Confirm',
      wizardTitleFallback: 'Wizard',

      productNamePlaceholder: 'Your product or brand name',
      presentProductPlaceholder: 'Holding, rotating, demonstrating features...',
      uploadPhotoLabel: 'Upload a clear product photo',
      uploadPhotoSectionLabel: 'Upload product photo',
      imageUploadHint: 'JPG or PNG, up to 10MB',
      imageUploadCta: 'Upload photo',
      imageUploadLoading: 'Uploading...',
      removeUploadedPhoto: 'Remove uploaded photo',
      removePhoto: 'Remove photo',
      uploadFailed: 'Upload Failed. Could not upload the image. Please try again.',
      productTitle: 'Product',
      productNameQuestion: "What's your product called?",
      productTypeQuestion: 'What is your product type?',
      productUsageQuestion: 'How should we present your product?',
      productPresentedQuestion: 'How should the product be presented?',

      personaTitle: 'Persona',
      personaGenderQuestion: 'What gender should the creator be?',
      personaAgeQuestion: 'What age range should the creator be?',
      personaNationQuestion: 'Which nation should the creator be from?',
      personaNationPlaceholder: 'e.g., Turkish, American, British',
      personaDescriptionLabel: 'Describe your dream person.',
      personaDescriptionPlaceholder: 'Briefly describe...',

      sceneTitle: 'Scene',
      sceneQuestion: 'Describe your dream scene.',
      scenePlaceholder: 'Briefly describe...',
      sceneTimeQuestion: 'What time of day?',
      sceneCameraQuestion: 'Which camera should the creator use?',
      sceneMovementQuestion: 'How should the creator move?',

      scriptTitle: 'Script',
      scriptQuestion: 'What should the creator say?',
      scriptPlaceholder: 'Write your script here...',
      scriptMinChars: 'Minimum {count} characters',
      scriptTipsTitle: 'Tips for a great script',
      scriptTip1: 'Start with a quick hook',
      scriptTip2: 'Keep it natural and conversational',
      scriptTip3: 'Mention 1-2 key benefits',
      scriptTip4: 'End with a clear call-to-action',

      finalizeTitle: 'Ready to Create!',
      finalizeSubtitle: 'Your UGC video is ready to be generated.',
      finalizeFeature1: '10-15 seconds HD video',
      finalizeFeature2: '9:16 vertical format for social',
      finalizeFeature3: 'Available for download for 14 days',
      finalizeThisWillUse: 'This will use',
      finalizeCredits: '{count} credits',
      createVideoFailed: 'Failed to create video. Please try again.',
      notEnoughCredits: 'Not enough credits. Please purchase more credits.',
      tooManyGenerating: 'You can generate up to 5 videos at the same time. Please wait for one of your active videos to finish, then try again.',
      tooManyRequests: 'Too many requests. Please wait a moment and try again.',
      loginToCreate: 'Please log in to create videos.',

      optionPhysical: 'Physical',
      optionDigital: 'Digital',
      optionService: 'Service',
      optionShowTalk: 'Show + Talk',
      optionTalk: 'Talk',
      optionFemale: 'Female',
      optionMale: 'Male',
      optionFrontCamera: 'Front Camera',
      optionBackCamera: 'Back Camera',
      optionStanding: 'Standing',
      optionWalking: 'Walking',
      optionSitting: 'Sitting',
      optionMorning: 'Morning',
      optionNoon: 'Noon',
      optionEvening: 'Evening',
      optionNight: 'Night',
    },
    video: {
      queued: 'Queued...',
      generating: 'Creating...',
      timedOut: 'Timed Out',
      failed: 'Failed',
      creditsRefunded: 'Credits refunded',
      videoAlt: 'Video',
      download: 'Download',
      menu: 'Menu',
      share: 'Share',
      delete: 'Delete',
      noShareLink: 'No share link available.',
      linkCopied: 'Video link copied.',
      copyFailed: 'Could not copy link.',
      shareFailed: 'Share failed. Please try again.',
      deleteConfirm: 'Are you sure you want to delete this video?',
      deleteFailed: 'Delete failed. Please try again.',
      deleteFailedVideoConfirm: 'Are you sure you want to delete this failed video?',
      failedDeleteAlert: 'Failed to delete video. Please try again.',
    },
  },
  tr: {
    app: {
      profile: 'Profil',
      supportAndHelp: 'Destek ve Yardım',
      closeModal: 'Modalı kapat',
    },
    header: {
      credits: 'Bakiye:',
      addCredits: 'Bakiye Ekle',
      upgrade: 'Yükselt',
    },
    tabbar: {
      create: 'Oluştur',
    },
    offer: {
      buyCta: '5 Videoyu Al',
      registerTitle: 'Önce Kayıt Ol',
      registerSubtitle: 'Devam etmek için hesabını oluştur',
      hero: {
        eyebrow: 'Sınırlı süreli giriş paketi',
        proofText: '1.000+ kişi satın aldı',
        proofRatingAria: '5 üzerinden 5 yıldız',
        title: 'İçerik Maliyetinizi %90 Düşürün: Satışları Katlayan UGC Videoları 5 Dakikada Cebinizde.',
        subtitle: 'Creator beklemeden, ürün kargolamadan ve uzun prodüksiyon süreciyle uğraşmadan; yeni hook, persona, script ve dil testlerini çok daha hızlı başlat.',
        highlights: [
          'Tek pakette 5 farklı dikey reklam videosu oluşturma imkanı',
          'Gerçekçi AI UGC görünümü ve güçlü ürün korunumu',
          'Farklı persona, ortam, hook ve script yönleriyle test imkanı',
          'Hızlı kreatif yenileme ve çoklu dil esnekliği',
        ],
        summary: {
          label: 'Giriş paketi fiyatı',
          oldPrice: '',
          note: 'Tek ödeme. Beş video. Ürünü hızlıca göstermek ve kaliteyi net görmek için tasarlandı.',
          badges: ['Tek seferlik paket', 'Hızlı kreatif başlangıç', 'Ürün odaklı çıktı'],
          includesTitle: '',
          includes: [],
        },
      },
      results: {
        eyebrow: '',
        title: 'Müşterilerimizin sonuçları',
        subtitle: '',
        note: '',
        tags: ['5 video', 'Gerçekçi AI UGC', 'Farklı açılar'],
        items: [
          {
            badge: 'Gerçekçilik',
            title: 'Yüz, akış ve konuşma doğal görünmeli',
            text: 'İlk izlenim şu olmalı: bu içerik paid social için gerçekten kullanılabilir görünüyor.',
            visualLabel: 'Sonuç videosu placeholder 01',
            visualNote: 'Bunu, creator tarzı konuşma planı olan gerçek bir çıktı videosu ile değiştir.',
          },
          {
            badge: 'Ürün korunumu',
            title: 'Ürünün videoda temiz kaldığını göster',
            text: 'Sahne AI ile üretilse bile ürünün bozulmadığını ve tanınabilir kaldığını göstermek burada kritik güven katmanı oluşturur.',
            visualLabel: 'Sonuç videosu placeholder 02',
            visualNote: 'Yüklenen ürün görselinin videoda stabil kaldığı bir örnekle değiştir.',
          },
          {
            badge: 'Hook çeşitliliği',
            title: 'Beş kopya değil, farklı açılar göster',
            text: 'Aynı fikrin varyasyonu gibi değil, farklı mesaj yönleri test edilebilen gerçek kreatif çeşitlilik gibi hissettirmeli.',
            visualLabel: 'Sonuç videosu placeholder 03',
            visualNote: 'İlk karttan farklı hook veya reklam açısı kullanan bir örnek koy.',
          },
          {
            badge: 'Format kalitesi',
            title: 'Jump cut ve b-roll ritmini göster',
            text: 'Videolar ham klip gibi değil, kısa-form reklam akışı gibi hissettirdiğinde teklif çok daha güçlü görünür.',
            visualLabel: 'Sonuç videosu placeholder 04',
            visualNote: 'Akış, tempo veya shot geçişi hissi veren bir çıktı ile değiştir.',
          },
          {
            badge: 'Çoklu dil',
            title: 'Aynı konsepti birden fazla dilde taşı',
            text: 'Tekrar prodüksiyon kurmadan aynı ürünün farklı pazarlara nasıl açılabileceğini göstermek teklifin değerini büyütür.',
            visualLabel: 'Sonuç videosu placeholder 05',
            visualNote: 'Yerelleştirilmiş çıktı veya yan yana dil varyasyonu örneği ekle.',
          },
        ],
      },
      problem: {
        eyebrow: 'Neden önemli',
        title: 'Geleneksel UGC süreci yavaş, pahalı ve ölçeklemesi zor',
        subtitle: 'Her yeni video daha fazla zaman, daha fazla koordinasyon ve daha fazla maliyet demek. Süreç yavaşladıkça daha az fikir test ediliyor ve kazanan kreatif daha geç bulunuyor.',
        cards: [
          {
            icon: 'clock',
            title: 'Yeni bir kreatifi yayına almak günler sürebiliyor',
            text: 'Creator bulmak, ürünü göndermek, brief vermek, çekimi beklemek ve revize toplamak derken basit bir reklam testi bile uzun bir prodüksiyon sürecine dönüşüyor.',
          },
          {
            icon: 'warning',
            title: 'Doğru persona, ortam ve ürünü aynı anda yakalamak zor',
            text: 'Video çıksa bile her şey yerli yerine oturmayabiliyor. Persona tam uymuyor, ortam istenen hissi vermiyor ya da ürün ekranda yeterince temiz görünmüyor.',
          },
          {
            icon: 'chart',
            title: 'Her yeni varyasyon daha fazla maliyet ve gecikme getiriyor',
            text: 'Kaliteli bir UGC video geleneksel yolda zaten pahalı. Farklı açılar, yeni scriptler veya çoklu dil istediğinde test süreci daha da yavaş ve maliyetli hale geliyor.',
          },
        ],
      },
      workflow: {
        eyebrow: 'Nasıl çalışır',
        title: 'Gerçekçi UGC videolar için 3 basit adım',
        subtitle: 'Fiziksel ürünler, dijital ürünler ve hizmetler için uygundur. Ürünü, persona, ortam ve script yönünü gir, onayla. AI agent sistemi videoyu yaklaşık 5 dakikada hazırlar.',
        items: [
          {
            title: 'Ürün detaylarını gir',
            text: 'Ürün adı, ürün tipi, hayalindeki persona, hayalindeki ortam ve videoda korunmasını istediğin ürün görselini ekle.',
          },
          {
            title: 'Creator ne söyleyecek onu yaz',
            text: 'Script yönünü belirle, mesajı netleştir ve videoda creator\'ın hangi açıyla konuşacağını tanımla.',
          },
          {
            title: 'Onayla ve üret',
            text: 'Kurulumu onayla, agent sistemi videoları dakikalar içinde üretsin; seni günler süren prodüksiyon döngüsüne sokmasın.',
          },
        ],
      },
      comparison: {
        eyebrow: 'Neden 5UGC',
        title: '5UGC ile daha hızlı, daha kolay',
        subtitle: 'Burada sadece 5 video almıyorsun. Ürünü yükleyip basit bir 3 adımlı akışla yaklaşık 5 dakikada gerçekçi videolar alıyorsun. Ürün ekranda daha stabil kalır, persona, açı ve dilleri çok daha hızlı test edersin.',
        matrixLabel: 'Karşılaştırma',
        traditionalTitle: 'Geleneksel',
        aiTitle: '5UGC',
        rows: [
          'Fiziksel ürünler dahil farklı ürün tiplerini, persona ve ortamlarla çok daha hızlı test et',
          'Daha gerçekçi videolar alırken yüklediğin ürün görseli ekranda çok daha stabil kalsın',
          '3 basit adımla kurul ve videolar yaklaşık 5 dakikada hazır olsun',
          'Jump cut, b-roll ve çoklu dil varyasyonlarını otomatik oluştur',
          'Her yeni açı için video başı $20-$60 ödemeden ve günler beklemeden ilerle',
        ],
      },
      testimonials: {
        eyebrow: 'Müşteri yorumu',
        title: '5UGC’yi deneyen kullanıcılar ne diyor',
        subtitle: '5UGC ile daha hızlı test yapan, ürünü videoda daha temiz koruyan ve yeni açıları daha az prodüksiyon yüküyle yayına alan ekiplerden kısa yorumlar.',
        items: [
          { quote: '“Dakikalar içinde test videosu görmek hızımızı değiştirdi.”', name: 'Selin K.', role: 'Performance marketer' },
          { quote: '“Ürün ekranda çoğu AI aracından çok daha temiz kaldı.”', name: 'Mert A.', role: 'E-ticaret kurucusu' },
          { quote: '“Bir öğleden sonra içinde üç farklı hook test ettik.”', name: 'Derya T.', role: 'Growth lead' },
          { quote: '“Kurulum o kadar basitti ki ekip hemen kullanmaya başladı.”', name: 'Cem I.', role: 'Marka yöneticisi' },
          { quote: '“Videolar paid ads için yeterince gerçekçi hissettirdi.”', name: 'Ece Y.', role: 'Paid social uzmanı' },
          { quote: '“Farklı personalar daha hızlı sinyal verdi.”', name: 'Burak S.', role: 'DTC operatörü' },
          { quote: '“Çoklu dil varyasyonları yeni pazar testini kolaylaştırdı.”', name: 'Lina P.', role: 'Pazaryeri yöneticisi' },
          { quote: '“Jump cut temposu kreatifleri daha doğal hissettirdi.”', name: 'Onur D.', role: 'Kreatif stratejist' },
          { quote: '“Ürün yüklemek videoları marka çizgisinde tuttu.”', name: 'Zeynep N.', role: 'İçerik lideri' },
          { quote: '“Beş video, kazanan açıyı hızlı görmek için yeterli çeşit verdi.”', name: 'Arda B.', role: 'Media buyer' },
          { quote: '“Yeni bir creator çekimi organize etmekten çok daha ekonomikti.”', name: 'Melis C.', role: 'Küçük işletme sahibi' },
          { quote: '“Yeni bir teklifi tüm ekibi yavaşlatmadan test edebildik.”', name: 'Kaan E.', role: 'Growth manager' },
        ],
      },
      faq: {
        eyebrow: 'SSS',
        title: '5UGC satın almadan önce en çok sorulan sorular',
        subtitle: 'Gerçekçilikten ürün korunmasına, hızdan 3 adımlı akışa ve çoklu dile kadar karar vermeyi kolaylaştıran net cevaplar.',
        items: [
          { question: '5UGC sadece fiziksel ürünler için mi?', answer: 'Hayır. 5UGC dijital ürünler ve hizmetler için de kullanılabilir. Ama özellikle fiziksel ürünlerde çok güçlüdür; çünkü yüklenen ürün görselini videoda daha stabil ve tanınabilir tutmaya odaklıdır.' },
          { question: 'Videolar fake görünür mü?', answer: 'Ürünün en güçlü taraflarından biri gerçekçilik. Amaç sıradan AI video üretmek değil; reklamlarda gerçekten test edilebilecek kadar kullanılabilir hissettiren UGC tarzı çıktılar vermek.' },
          { question: 'Ürün videoda bozulur mu?', answer: 'Sistem yüklenen ürün görselini mümkün olduğunca koruyacak şekilde çalışır. Arka planda yaptığımız ek işlemler sayesinde ürün, klasik AI video akışlarına göre çok daha temiz ve stabil kalır.' },
          { question: 'Videolar ne kadar sürede oluşuyor?', answer: 'Videolar genelde yaklaşık 5 dakikada hazırlanır. Bu hız ürünün ana değerlerinden biri olduğu için markanın adı da 5UGC.' },
          { question: 'Neden sadece 3 adım var?', answer: 'Çünkü asıl ağır işi arka planda güçlü bir AI agent sistemi yapıyor. Kullanıcıyı uzun bir kurulum sürecine sokmak yerine akış üç basit adıma indiriliyor: kurulum, script ve onay.' },
          { question: 'İlk adımda tam olarak ne giriyorum?', answer: 'İlk adımda ürün adı, ürün tipi, hayalindeki persona, hayalindeki ortam ve videoda korunmasını istediğin ürün görselini giriyorsun.' },
          { question: 'İkinci ve üçüncü adımda ne oluyor?', answer: 'İkinci adımda creator\'ın ne söyleyeceğini ve mesaj açısını yazıyorsun. Üçüncü adımda ise kurulumu gözden geçirip üretimi onaylıyorsun.' },
          { question: 'Jump cut ve b-roll otomatik mi ekleniyor?', answer: 'Evet. Jump cut ritmi ve b-roll benzeri yapı otomatik hazırlanır. Böylece çıktı ham bir klip gibi değil, test etmeye daha hazır kısa-form reklam gibi hissettirir.' },
          { question: 'Çoklu dil desteği var mı?', answer: 'Evet. Çoklu dil desteği var. Bu da aynı teklifi veya aynı konsepti tüm prodüksiyonu baştan kurmadan farklı kitlelerde test etmeyi kolaylaştırır.' },
          { question: 'Geleneksel UGC ile maliyet farkı ne?', answer: 'Geleneksel creator akışında bir video alt seviyede bile rahatça $20-$30 olabilir. 5UGC kalitesine yaklaşan işlerde bu rakam çoğu zaman $50-$60 seviyelerine çıkar. Bu da hızlı test sürecini çok pahalı hale getirir.' },
          { question: 'Bu sayfa abonelik mi satıyor?', answer: 'Hayır. Bu sayfa daha düşük bariyerli, tek seferlik bir giriş paketi satıyor. Amaç kullanıcıyı güçlü bir ilk deneyimle içeri almak. Düzenli kullanım ihtiyacında abonelik planları sonraki doğal adım olur.' },
        ],
      },
      final: {
        eyebrow: 'Teste başla',
        title: '5UGC\'nin gerçekten ne yapabildiğini görmek istiyorsan buradan başla',
        subtitle: '5 videoluk giriş paketini al, kaliteyi kendi gözünle gör ve küçük bir satın alımı dakikalar içinde gerçek kreatif proof\'a çevir.',
        note: 'Hazır olduğunda sayfadaki herhangi bir CTA\'yı veya alttaki sabit butonu kullan.',
      },
    },
    home: {
      library: 'Kütüphane',
      avatarsComingSoon: 'Avatarlar - Yakında',
      createCardTitle: 'Yeni video',
      createCardSubtitle: 'Yeni bir fikri dakikalar içinde UGC\'ye çevir.',
      sorting: 'Sıralama',
      loadingVideos: 'Videolar yükleniyor...',
      retry: 'Tekrar Dene',
      couldNotLoadVideos: 'Videolar yüklenemedi',
      createVideoCta: 'Video Oluştur',
      legalPrefix: '',
      legalAnd: 've',
    },
    success: {
      iconAlt: 'Başarılı ödeme',
      title: 'Aboneliğin başladı',
      subtitle: 'Ödemen başarıyla tamamlandı. Artık haftalık kredilerini kullanıp UGC videoları oluşturmaya devam edebilirsin.',
      goHome: 'Kütüphaneye git',
    },
    profileCard: {
      user: 'Kullanıcı',
      avatar: 'Avatar',
      availableCredits: 'Kullanılabilir Bakiye',
      buyCredits: 'Bakiye Satın Al',
      upgradePlan: 'Planı Yükselt',
    },
    settings: {
      shareFeedback: 'Görüşünü Paylaş',
      manageSubscription: 'Aboneliği Yönet',
      changeLanguage: 'Uygulama Dilini Değiştir',
      helpCenter: 'Yardım Merkezi',
      supportChat: 'Destek Sohbeti',
      signOut: 'Çıkış Yap',
      languageModalTitle: 'Uygulama Dilini Değiştir',
      languageEnglish: 'İngilizce (US)',
      languageTurkish: 'Türkçe',
    },
    auth: {
      signIn: 'Giriş Yap',
      signInSubtitle: 'Hesabına giriş yap',
      signUp: 'Kayıt Ol',
      signUpSubtitle: 'Yeni bir hesap oluştur',
      resetPassword: 'Şifre Sıfırla',
      resetPasswordSubtitle: 'Şifre sıfırlama bağlantısını e-postana göndereceğiz.',
      noAccount: 'Hesabın yok mu?',
      hasAccount: 'Zaten hesabın var mı?',
      forgotPassword: 'Şifremi Unuttum?',
      backToSignIn: 'Giriş ekranına dön',
      email: 'E-posta',
      password: 'Şifre',
      fullName: 'Ad Soyad',
      sendLink: 'Bağlantı Gönder',
      sending: 'Gönderiliyor...',
      resendIn: '{time} sonra tekrar gönder',
      fillAllFields: 'Lütfen tüm alanları doldur.',
      passwordMin: 'Şifre en az 8 karakter olmalı.',
      creatingAccount: 'Hesap oluşturuluyor...',
      signingIn: 'Giriş yapılıyor...',
      accountCreatedCheckEmail: 'Hesap oluşturuldu! Lütfen e-postanı kontrol et.',
      enterEmail: 'Lütfen e-posta adresini gir.',
      resetLinkSent: 'Şifre sıfırlama bağlantısı e-postana gönderildi.',
      legalPrefix: 'Kayıt olarak aşağıdakileri kabul etmiş olursunuz:',
      legalAnd: 've',
      termsOfService: 'Hizmet Şartları',
      privacyPolicy: 'Gizlilik Politikası',
    },
    paywall: {
      subscription: 'Abonelik',
      choosePlan: 'Planını Seç',
      unlockMore: 'Haftalık kaç video üretmek istediğine göre planını seç',
      creditInfo: 'Her 1 video için 10 bakiye kullanılır.',
      continue: 'Devam Et',
      processing: 'İşleniyor...',
      checkoutFailed: 'Ödeme ekranı açılamadı. Lütfen tekrar dene.',
      checkoutFailedInline: 'Ödeme alınamadı. Lütfen tekrar dene.',
      popular: 'Popüler',
      level1: 'Level 1',
      level2: 'Level 2',
      level3: 'Level 3',
      weekly: '/hafta',
      level1Features: 'Haftalık 2 video',
      level2Features: 'Haftalık 6 video',
      level3Features: 'Haftalık 23 video',
    },
    subscriptionModal: {
      title: 'Aboneliğin',
      currentPlan: 'Mevcut plan',
      description: 'Haftalık daha fazla video ihtiyacın olursa istediğin zaman üst plana geçebilirsin.',
      upgradeButton: 'Aboneliği Yükselt',
      manageButton: 'Aboneliği Yönet',
      unknownPlan: 'Mevcut Abonelik',
    },
    videoModal: {
      closeVideoModal: 'Video modalını kapat',
      untitledAsset: 'Adsız İçerik',
      download: 'İndir',
      shareVideo: 'Videoyu paylaş',
      deleteVideo: 'Videoyu sil',
      noShareLinkForVideo: 'Bu video için paylaşım bağlantısı yok.',
      linkCopiedClipboard: 'Video bağlantısı panoya kopyalandı.',
      copyLinkManually: 'Bağlantı kopyalanamadı. Lütfen manuel kopyala.',
      shareFailed: 'Paylaşım başarısız. Lütfen tekrar dene.',
      type: 'Tür',
      duration: 'Süre',
      audio: 'Ses',
      aspectRatio: 'En/Boy Oranı',
      dimensions: 'Boyutlar',
      generated: 'Oluşturulma',
      retention: 'Saklama',
      included: 'Dâhil',
      daysLeft: '{count} gün kaldı',
      dayLeft: '{count} gün kaldı',
      videoAsset: 'VİDEO İÇERİĞİ',
      details: 'DETAYLAR',
      videoTypeMp4: 'Video / MP4',
    },
    wizard: {
      product: 'Ürün',
      persona: 'Persona',
      scene: 'Sahne',
      script: 'Metin',
      finalize: 'Son',
      back: 'Geri',
      closeWizard: 'Sihirbazı kapat',
      next: 'İleri',
      confirm: 'Onayla',
      wizardTitleFallback: 'Sihirbaz',

      productNamePlaceholder: 'Ürün veya marka adın',
      presentProductPlaceholder: 'Elde tutma, çevirme, özellik gösterimi...',
      uploadPhotoLabel: 'Net bir ürün fotoğrafı yükle',
      uploadPhotoSectionLabel: 'Ürün fotoğrafı yükle',
      imageUploadHint: 'JPG veya PNG, en fazla 10MB',
      imageUploadCta: 'Fotoğraf yükle',
      imageUploadLoading: 'Yükleniyor...',
      removeUploadedPhoto: 'Yüklenen fotoğrafı kaldır',
      removePhoto: 'Fotoğrafı kaldır',
      uploadFailed: 'Yükleme başarısız. Fotoğraf yüklenemedi. Lütfen tekrar dene.',
      productTitle: 'Ürün',
      productNameQuestion: 'Ürününün adı nedir?',
      productTypeQuestion: 'Ürün türü nedir?',
      productUsageQuestion: 'Ürününü nasıl sunalım?',
      productPresentedQuestion: 'Ürün nasıl gösterilsin?',

      personaTitle: 'Persona',
      personaGenderQuestion: 'İçerik üreticisinin cinsiyeti ne olmalı?',
      personaAgeQuestion: 'İçerik üreticisinin yaş aralığı ne olmalı?',
      personaNationQuestion: 'İçerik üreticisi hangi ülkeden olmalı?',
      personaNationPlaceholder: 'örn. Türk, Amerikan, İngiliz',
      personaDescriptionLabel: 'Hayalindeki kişiyi anlat.',
      personaDescriptionPlaceholder: 'Kısaca anlat...',

      sceneTitle: 'Sahne',
      sceneQuestion: 'Hayalindeki sahneyi anlat.',
      scenePlaceholder: 'Kısaca anlat...',
      sceneTimeQuestion: 'Günün hangi zamanı?',
      sceneCameraQuestion: 'İçerik üreticisi hangi kamerayı kullanmalı?',
      sceneMovementQuestion: 'İçerik üreticisi nasıl hareket etmeli?',

      scriptTitle: 'Metin',
      scriptQuestion: 'İçerik üreticisi ne söylemeli?',
      scriptPlaceholder: 'Metnini buraya yaz...',
      scriptMinChars: 'Minimum {count} karakter',
      scriptTipsTitle: 'İyi bir metin için ipuçları',
      scriptTip1: 'Hızlı bir girişle dikkat çek',
      scriptTip2: 'Doğal ve sohbet tonunda tut',
      scriptTip3: '1-2 temel faydayı mutlaka belirt',
      scriptTip4: 'Net bir aksiyon çağrısı ile bitir',

      finalizeTitle: 'Oluşturmaya Hazır!',
      finalizeSubtitle: 'UGC videon oluşturulmaya hazır.',
      finalizeFeature1: '10-15 saniye HD video',
      finalizeFeature2: 'Sosyal medya için 9:16 dikey format',
      finalizeFeature3: '14 gün boyunca indirilebilir',
      finalizeThisWillUse: 'Kullanılacak bakiye',
      finalizeCredits: '{count} bakiye',
      createVideoFailed: 'Video oluşturulamadı. Lütfen tekrar dene.',
      notEnoughCredits: 'Yeterli bakiye yok. Lütfen daha fazla bakiye satın al.',
      tooManyGenerating: 'Aynı anda en fazla 5 video oluşturabilirsiniz. Lütfen aktif videolarınızdan birinin tamamlanmasını bekleyin ve ardından tekrar deneyin.',
      tooManyRequests: 'Çok fazla istek gönderildi. Lütfen biraz bekleyip tekrar dene.',
      loginToCreate: 'Video oluşturmak için lütfen giriş yap.',

      optionPhysical: 'Fiziksel',
      optionDigital: 'Dijital',
      optionService: 'Hizmet',
      optionShowTalk: 'Göster + Anlat',
      optionTalk: 'Anlat',
      optionFemale: 'Kadın',
      optionMale: 'Erkek',
      optionFrontCamera: 'Ön Kamera',
      optionBackCamera: 'Arka Kamera',
      optionStanding: 'Ayakta',
      optionWalking: 'Yürürken',
      optionSitting: 'Oturarak',
      optionMorning: 'Sabah',
      optionNoon: 'Öğle',
      optionEvening: 'Akşam',
      optionNight: 'Gece',
    },
    video: {
      queued: 'Kuyrukta...',
      generating: 'Oluşturuluyor...',
      timedOut: 'Süre Aşımına Uğradı',
      failed: 'Başarısız',
      creditsRefunded: 'Bakiye iade edildi',
      videoAlt: 'Video',
      download: 'İndir',
      menu: 'Menu',
      share: 'Paylaş',
      delete: 'Sil',
      noShareLink: 'Paylaşım bağlantısı yok.',
      linkCopied: 'Video bağlantısı kopyalandı.',
      copyFailed: 'Bağlantı kopyalanamadı.',
      shareFailed: 'Paylaşım başarısız. Lütfen tekrar dene.',
      deleteConfirm: 'Bu videoyu silmek istediğine emin misin?',
      deleteFailed: 'Silme işlemi başarısız. Lütfen tekrar dene.',
      deleteFailedVideoConfirm: 'Bu başarısız videoyu silmek istediğine emin misin?',
      failedDeleteAlert: 'Video silinemedi. Lütfen tekrar dene.',
    },
  },
};
